import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add,
  Edit,
  Visibility,
  Delete,
  CheckCircle,
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Assessment,
  Search,
  FilterList,
  Refresh
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import AccountHeadModal from './AccountHeadModal';
import AssetModal from './AssetModal';
import CapitalModal from './CapitalModal';
import OtherOptionModal from './OtherOptionModal';
import BalanceSheetHeadModal from './BalanceSheetHeadModal';
import './AccountHeadDashboard.css';

const AccountHeadDashboard = () => {
  // Session validation
  useSessionValidation();
  
  const { companyId, token, userId } = useAuth();
  
  // Helper function to format date as dd-MM-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'create', 'edit', 'view'
  const [selectedItem, setSelectedItem] = useState(null);

  // Delete confirmation modal states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Data states
  const [accountHeads, setAccountHeads] = useState([]);
  const [assets, setAssets] = useState([]);
  const [capitals, setCapitals] = useState([]);
  const [otherOptions, setOtherOptions] = useState([]);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [mainHeads, setMainHeads] = useState([]);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPayableReceivable, setFilterPayableReceivable] = useState('all');
  const [filterBalanceSheetHead, setFilterBalanceSheetHead] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // API endpoints
  const accountHeadApiEndpoint = 'IncomeExpenseApi';
  const assetApiEndpoint = 'AssetsApi';
  const capitalApiEndpoint = 'CapitalApi';
  const otherOptionApiEndpoint = 'OthersApi';
  const balanceSheetApiEndpoint = 'BalanceSheetHeadApi';
  const incomeExpenseApiEndpoint = 'IncomeExpenseApi';

  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  const fetchAccountHeads = async () => {
    try {
      console.log('Fetching Account Heads with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(accountHeadApiEndpoint, `/${companyId}`, token);
      console.log('Account Heads Response:', response);
      const data = response.response_data || response || [];
      console.log('Account Heads Data:', data);
      setAccountHeads(data);
    } catch (error) {
      console.error('Error fetching account heads:', error);
      setAccountHeads([]);
    }
  };

  const fetchAssets = async () => {
    try {
      console.log('Fetching Assets with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(assetApiEndpoint, `/${companyId}`, token);
      console.log('Assets Response:', response);
      const data = response.response_data || response || [];
      console.log('Assets Data:', data);
      setAssets(data);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    }
  };

  const fetchCapitals = async () => {
    try {
      console.log('Fetching Capitals with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(capitalApiEndpoint, `/${companyId}`, token);
      console.log('Capital API Response:', response);
      console.log('Capital Data:', response.response_data || response || []);
      const data = response.response_data || response || [];
      setCapitals(data);
    } catch (error) {
      console.error('Error fetching capitals:', error);
      setCapitals([]);
    }
  };

  const fetchOtherOptions = async () => {
    try {
      console.log('Fetching Other Options with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(otherOptionApiEndpoint, `/${companyId}`, token);
      console.log('Other Options API Response:', response);
      console.log('Other Options Data:', response.response_data || response || []);
      const data = response.response_data || response || [];
      setOtherOptions(data);
    } catch (error) {
      console.error('Error fetching other options:', error);
      setOtherOptions([]);
    }
  };

  const fetchBalanceSheetHeads = async () => {
    try {
      console.log('Fetching Balance Sheet Heads with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(balanceSheetApiEndpoint, `/${companyId}`, token);
      console.log('Balance Sheet Heads Response:', response);
      const data = response.response_data || response || [];
      console.log('Balance Sheet Heads Data:', data);
      setBalanceSheetHeads(data);
    } catch (error) {
      console.error('Error fetching balance sheet heads:', error);
      setBalanceSheetHeads([]);
    }
  };

  const fetchMainHeads = async () => {
    try {
      console.log('Fetching Main Heads with:', { companyId, token: token ? 'present' : 'missing' });
      const response = await fetchData(incomeExpenseApiEndpoint, `/MainHead/${companyId}`, token);
      console.log('Main Heads Response:', response);
      const data = response.response_data || response || [];
      console.log('Main Heads Data:', data);
      setMainHeads(data);
    } catch (error) {
      console.error('Error fetching main heads:', error);
      setMainHeads([]);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Fetching accounts data...');
      setLoadingVariant('default');
      
      await Promise.allSettled([
        fetchAccountHeads(),
        fetchAssets(),
        fetchCapitals(),
        fetchOtherOptions(),
        fetchBalanceSheetHeads(),
        fetchMainHeads()
      ]);
      
      setLoadingMessage('Accounts data loaded successfully!');
      setLoadingVariant('success');
      
      setTimeout(() => {
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingMessage('Failed to load accounts data!');
      setLoadingVariant('error');
      setError('Failed to fetch accounts data. Please try again.');
      
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  // Fetch single item by ID for view/edit
  const fetchSingleItem = async (type, id) => {
    try {
      let endpoint;
      switch (type) {
        case 'asset':
          endpoint = assetApiEndpoint;
          break;
        case 'capital':
          endpoint = capitalApiEndpoint;
          break;
        case 'otherOption':
          endpoint = otherOptionApiEndpoint;
          break;
        case 'balanceSheetHead':
          endpoint = balanceSheetApiEndpoint;
          break;
        default:
          endpoint = accountHeadApiEndpoint;
      }
      const response = await fetchData(endpoint, `/GetById/${id}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error(`Error fetching ${type} by ID:`, error);
      return null;
    }
  };

  const handleOpenModal = async (type, action, item = null) => {
    setModalAction(action);
    
    // If viewing or editing, fetch fresh data from API
    if ((action === 'view' || action === 'edit') && item?.Id) {
      setLoading(true);
      setLoadingMessage(`Fetching ${type} details...`);
      setLoadingVariant('default');
      
      const freshData = await fetchSingleItem(type, item.Id);
      
      if (freshData) {
        setSelectedItem({ ...freshData, type });
        setFormData({ ...freshData, type });
      } else {
        // Fallback to existing item data if fetch fails
        setSelectedItem({ ...item, type });
        setFormData({ ...item, type } || {});
      }
      
      setLoading(false);
    } else {
      setSelectedItem({ ...item, type });
      setFormData({ ...item, type } || { type });
    }
    
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    
    try {
      const itemType = formData.type || 'accountHead';
      let endpoint, entityName;
      
      switch (itemType) {
        case 'asset':
          endpoint = assetApiEndpoint;
          entityName = 'asset';
          break;
        case 'capital':
          endpoint = capitalApiEndpoint;
          entityName = 'capital';
          break;
        case 'otherOption':
          endpoint = otherOptionApiEndpoint;
          entityName = 'other option';
          break;
        case 'balanceSheetHead':
          endpoint = balanceSheetApiEndpoint;
          entityName = 'balance sheet head';
          break;
        default:
          endpoint = accountHeadApiEndpoint;
          entityName = 'account';
      }
      
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      
      setLoadingMessage(`${actionText} ${entityName}...`);
      setLoadingVariant('default');
      
      // Prepare data with PascalCase field names for API
      let dataToSend = { ...formData, CompanyId: companyId };
      delete dataToSend.type; // Remove type field before sending to API
      
      // Convert numeric and other fields for account heads
      if (itemType === 'accountHead') {
        if (dataToSend.MainHeadId) {
          dataToSend.MainHeadId = parseInt(dataToSend.MainHeadId) || null;
        }
        if (dataToSend.BalanceSheetHeadId) {
          dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
        }
        if (dataToSend.VATRate) {
          dataToSend.VATRate = parseFloat(dataToSend.VATRate) || 0;
        }
        // Ensure boolean fields are properly set
        dataToSend.IsExpenseOrIncome = Boolean(dataToSend.IsExpenseOrIncome);
        dataToSend.IsTrading = Boolean(dataToSend.IsTrading);
      }
      
      // Convert numeric fields for assets, capitals, and other options
      if (itemType === 'asset') {
        if (dataToSend.DepreciationRate) {
          dataToSend.DepreciationRate = parseFloat(dataToSend.DepreciationRate) || 0;
        }
        if (dataToSend.OpeningBalance) {
          dataToSend.OpeningBalance = parseFloat(dataToSend.OpeningBalance) || 0;
        }
        if (dataToSend.BalanceSheetHeadId) {
          dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
        }
      } else if (itemType === 'capital' || itemType === 'otherOption') {
        if (dataToSend.OpeningBalance) {
          dataToSend.OpeningBalance = parseFloat(dataToSend.OpeningBalance) || 0;
        }
        if (dataToSend.BalanceSheetHeadId) {
          dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
        }
        // Only process MasterTypeId for otherOption items, not for capital
        if (itemType === 'otherOption' && dataToSend.MasterTypeId) {
          dataToSend.MasterTypeId = parseInt(dataToSend.MasterTypeId) || null;
        }
      }
      
      // Set CreatedBy for POST (create) or ModifiedBy for PUT/PATCH (edit)
      if (modalAction === 'create') {
        dataToSend.CreatedBy = userId;
      } else {
        dataToSend.ModifiedBy = userId;
      }
      
      if (modalAction === 'create') {
        await postData(endpoint, '', dataToSend, token);
        
        setLoadingMessage(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} created successfully!`);
        setLoadingVariant('success');
        setSuccess(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} created successfully!`);
        
      } else if (modalAction === 'edit') {
        const itemId = selectedItem?.Id || selectedItem?.id || formData?.Id || formData?.id;
        if (!itemId) {
          throw new Error('Item ID is missing');
        }
        // Ensure Id is included in the data for update
        dataToSend.Id = itemId;
        await updateData(endpoint, itemId, dataToSend, token);
        
        setLoadingMessage(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} updated successfully!`);
        setLoadingVariant('success');
        setSuccess(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} updated successfully!`);
      }
      
      // Close modal and refresh data
      handleCloseModal();
      
      // Refresh the specific data type
      if (itemType === 'asset') {
        await fetchAssets();
      } else if (itemType === 'capital') {
        await fetchCapitals();
      } else if (itemType === 'otherOption') {
        await fetchOtherOptions();
      } else if (itemType === 'balanceSheetHead') {
        await fetchBalanceSheetHeads();
      } else {
        await fetchAccountHeads();
      }
      
      // Set loading to false after successful operation
      setLoading(false);
      
    } catch (error) {
      console.error('Error saving:', error);
      setLoadingMessage('Operation failed!');
      setLoadingVariant('error');
      setError(`Failed to ${modalAction} ${
        formData.type === 'asset' ? 'asset' : 
        formData.type === 'capital' ? 'capital' : 
        formData.type === 'otherOption' ? 'other option' : 
        formData.type === 'balanceSheetHead' ? 'balance sheet head' : 'account'
      }. Please try again.`);
      
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  const handleDeleteClick = (type, item) => {
    setDeleteType(type);
    setDeleteItem(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const { type, item } = { type: deleteType, item: deleteItem };
      let endpoint, entityName;
      
      switch (type) {
        case 'asset':
          endpoint = assetApiEndpoint;
          entityName = 'asset';
          break;
        case 'capital':
          endpoint = capitalApiEndpoint;
          entityName = 'capital';
          break;
        case 'otherOption':
          endpoint = otherOptionApiEndpoint;
          entityName = 'other option';
          break;
        case 'balanceSheetHead':
          endpoint = balanceSheetApiEndpoint;
          entityName = 'balance sheet head';
          break;
        default:
          endpoint = accountHeadApiEndpoint;
          entityName = 'account';
      }
      
      setLoading(true);
      setError('');
      
      setLoadingMessage(`Deleting ${entityName}...`);
      setLoadingVariant('warning');
      
      await deleteData(endpoint, item.Id, token);
      
      setLoadingMessage(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} deleted successfully!`);
      setLoadingVariant('success');
      
      // Remove from local state
      if (type === 'asset') {
        const filteredData = assets.filter(i => i.Id !== item.Id);
        setAssets(filteredData);
      } else if (type === 'capital') {
        const filteredData = capitals.filter(i => i.Id !== item.Id);
        setCapitals(filteredData);
      } else if (type === 'otherOption') {
        const filteredData = otherOptions.filter(i => i.Id !== item.Id);
        setOtherOptions(filteredData);
      } else if (type === 'balanceSheetHead') {
        const filteredData = balanceSheetHeads.filter(i => i.Id !== item.Id);
        setBalanceSheetHeads(filteredData);
      } else {
        const filteredData = accountHeads.filter(i => i.Id !== item.Id);
        setAccountHeads(filteredData);
      }
      
      setSuccess(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} deleted successfully!`);
      
      // Close confirmation modal
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType('');
      
      // Brief success display
      setTimeout(() => {
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error deleting:', error);
      setLoadingMessage('Delete operation failed!');
      setLoadingVariant('error');
      setError(`Failed to delete ${
        deleteType === 'asset' ? 'asset' : 
        deleteType === 'capital' ? 'capital' : 
        deleteType === 'otherOption' ? 'other option' : 
        deleteType === 'balanceSheetHead' ? 'balance sheet head' : 'account'
      }. Please try again.`);
      
      // Close confirmation modal
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setDeleteType('');
      
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteItem(null);
    setDeleteType('');
  };

  const handleActivate = async (type, item) => {
    try {
      let endpoint, entityName;
      
      switch (type) {
        case 'asset':
          endpoint = assetApiEndpoint;
          entityName = 'asset';
          break;
        case 'capital':
          endpoint = capitalApiEndpoint;
          entityName = 'capital';
          break;
        case 'otherOption':
          endpoint = otherOptionApiEndpoint;
          entityName = 'other option';
          break;
        case 'balanceSheetHead':
          endpoint = balanceSheetApiEndpoint;
          entityName = 'balance sheet head';
          break;
        default:
          endpoint = accountHeadApiEndpoint;
          entityName = 'account';
      }
      
      setLoading(true);
      setError('');
      
      setLoadingMessage(`Activating ${entityName}...`);
      setLoadingVariant('default');
      
      await activateData(endpoint, item.Id, token);
      
      setLoadingMessage(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} activated successfully!`);
      setLoadingVariant('success');
      
      // Update local state
      if (type === 'asset') {
        const updatedData = assets.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
        setAssets(updatedData);
      } else if (type === 'capital') {
        const updatedData = capitals.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
        setCapitals(updatedData);
      } else if (type === 'otherOption') {
        const updatedData = otherOptions.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
        setOtherOptions(updatedData);
      } else if (type === 'balanceSheetHead') {
        const updatedData = balanceSheetHeads.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
        setBalanceSheetHeads(updatedData);
      } else {
        const updatedData = accountHeads.map(i => i.Id === item.Id ? { ...i, IsActive: true } : i);
        setAccountHeads(updatedData);
      }
      
      setSuccess(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} activated successfully!`);
      
      // Brief success display
      setTimeout(() => {
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error activating:', error);
      setLoadingMessage('Activate operation failed!');
      setLoadingVariant('error');
      setError(`Failed to activate ${
        type === 'asset' ? 'asset' : 
        type === 'capital' ? 'capital' : 
        type === 'otherOption' ? 'other option' : 
        type === 'balanceSheetHead' ? 'balance sheet head' : 'account'
      }. Please try again.`);
      
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const getEntityDisplayName = (type) => {
    switch (type) {
      case 'asset':
        return 'Asset';
      case 'capital':
        return 'Capital';
      case 'otherOption':
        return 'Other Option';
      case 'balanceSheetHead':
        return 'Balance Sheet Head';
      default:
        return 'Account';
    }
  };

  const getFilteredData = (data, type) => {
    let filtered = data;
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.SearchableName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterActive !== 'all') {
      filtered = filtered.filter(item => 
        filterActive === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (type === 'accountHead' && filterBalanceSheetHead !== 'all') {
      filtered = filtered.filter(item => 
        item.BalanceSheetHeadId === parseInt(filterBalanceSheetHead)
      );
    }

    if (type === 'asset' && filterPayableReceivable !== 'all') {
      filtered = filtered.filter(item => 
        filterPayableReceivable === 'payable_receivable' ? item.IsPayableOrReceivable : !item.IsPayableOrReceivable
      );
    }

    if ((type === 'capital' || type === 'otherOption') && filterPayableReceivable !== 'all') {
      filtered = filtered.filter(item => 
        filterPayableReceivable === 'payable_receivable' ? item.IsPayableOrReceivable : !item.IsPayableOrReceivable
      );
    }

    // Filter by Balance Sheet Head (Option Head)
    if (filterBalanceSheetHead !== 'all' && (type === 'asset' || type === 'capital' || type === 'otherOption')) {
      filtered = filtered.filter(item => 
        item.BalanceSheetHeadId === parseInt(filterBalanceSheetHead)
      );
    }
    
    return filtered;
  };

  const getPaginatedData = (data, type) => {
    const filtered = getFilteredData(data, type);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      data: filtered.slice(startIndex, endIndex),
      totalItems: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const renderPagination = (totalPages, totalItems) => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <span>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
            className="items-per-page-select"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button onClick={() => handlePageChange(1)} className="pagination-btn">1</button>
              {startPage > 2 && <span className="pagination-ellipsis">...</span>}
            </>
          )}
          
          {pageNumbers.map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
              <button onClick={() => handlePageChange(totalPages)} className="pagination-btn">{totalPages}</button>
            </>
          )}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Reset filters when switching tabs
    setSearchTerm('');
    setFilterActive('all');
    setFilterBalanceSheetHead('all');
    setFilterPayableReceivable('all');
    // Reset pagination
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setLoadingMessage('Refreshing data...');
    setLoadingVariant('default');
    fetchAllData();
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  // Get statistics
  const getStatistics = () => {
    // Account Head Statistics
    const totalAccounts = accountHeads.length;
    const activeAccounts = accountHeads.filter(item => item.IsActive).length;
    
    // For chart data, use the actual separate arrays instead of filtering accountHeads by type
    const capital = capitals.length; // Use capitals array directly
    const accountAssets = assets.length; // Use assets array directly  
    const others = otherOptions.length; // Use otherOptions array directly

    // Asset Statistics
    const totalAssets = assets.length;
    const activeAssets = assets.filter(item => item.IsActive).length;
    const payableReceivableAssets = assets.filter(item => item.IsPayableOrReceivable).length;
    const totalAssetValue = assets.reduce((sum, item) => sum + (parseFloat(item.OpeningBalance) || 0), 0);

    // Capital Statistics
    const totalCapitals = capitals.length;
    const activeCapitals = capitals.filter(item => item.IsActive).length;
    const payableReceivableCapitals = capitals.filter(item => item.IsPayableOrReceivable).length;
    const totalCapitalValue = capitals.reduce((sum, item) => sum + (parseFloat(item.OpeningBalance) || 0), 0);

    // Other Options Statistics
    const totalOtherOptions = otherOptions.length;
    const activeOtherOptions = otherOptions.filter(item => item.IsActive).length;
    const payableReceivableOtherOptions = otherOptions.filter(item => item.IsPayableOrReceivable).length;
    const totalOtherOptionsValue = otherOptions.reduce((sum, item) => sum + (parseFloat(item.OpeningBalance) || 0), 0);

    // Balance Sheet Head Statistics
    const totalBalanceSheetHeads = balanceSheetHeads.length;
    const activeBalanceSheetHeads = balanceSheetHeads.filter(item => item.IsActive).length;

    // Combined Statistics for Account Status Overview Chart
    const totalAllAccounts = totalAccounts + totalAssets + totalCapitals + totalOtherOptions + totalBalanceSheetHeads;
    const activeAllAccounts = activeAccounts + activeAssets + activeCapitals + activeOtherOptions + activeBalanceSheetHeads;

    // Debug logging
    console.log('Chart Data Debug:', {
      capital, accountAssets, others,
      totalAccounts, activeAccounts,
      totalAssets, activeAssets,
      totalCapitals, activeCapitals,
      totalOtherOptions, activeOtherOptions,
      'Raw Data Lengths': {
        accountHeads: accountHeads.length,
        assets: assets.length,
        capitals: capitals.length,
        otherOptions: otherOptions.length,
        balanceSheetHeads: balanceSheetHeads.length
      },
      'Chart uses separate arrays now': {
        'capitals array length': capitals.length,
        'assets array length': assets.length, 
        'otherOptions array length': otherOptions.length
      },
      'Combined Account Status': {
        'totalAllAccounts': totalAllAccounts,
        'activeAllAccounts': activeAllAccounts,
        'inactiveAllAccounts': totalAllAccounts - activeAllAccounts
      }
    });

    return { 
      totalAccounts, activeAccounts, capital, accountAssets, others,
      totalAssets, activeAssets, payableReceivableAssets, totalAssetValue,
      totalCapitals, activeCapitals, payableReceivableCapitals, totalCapitalValue,
      totalOtherOptions, activeOtherOptions, payableReceivableOtherOptions, totalOtherOptionsValue,
      totalBalanceSheetHeads, activeBalanceSheetHeads,
      // Combined totals for Account Status Overview chart
      totalAllAccounts, activeAllAccounts
    };
  };

  const stats = getStatistics();
  
  // Add useEffect to log data changes
  useEffect(() => {
    console.log('Data State Update:', {
      accountHeads: accountHeads.length,
      assets: assets.length,
      capitals: capitals.length,
      otherOptions: otherOptions.length,
      balanceSheetHeads: balanceSheetHeads.length,
      stats
    });
  }, [accountHeads, assets, capitals, otherOptions, balanceSheetHeads, stats]);
  
  const renderOverview = () => (
    <div className="overview-section">
      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card" onClick={() => handleTabChange('accountHeads')}>
          <div className="stat-icon blue-icon">
            <AccountBalance />
          </div>
          <div className="stat-content">
            <span className="stat-label">Account Head</span>
            <span className="stat-number">{stats.totalAccounts}</span>
          </div>
        </div>
        
        <div className="stat-card" onClick={() => handleTabChange('assets')}>
          <div className="stat-icon orange-icon">
            <Assessment />
          </div>
          <div className="stat-content">
            <span className="stat-label">Assets</span>
            <span className="stat-number">{stats.totalAssets}</span>
          </div>
        </div>
        
        
        <div className="stat-card" onClick={() => handleTabChange('capitals')}>
          <div className="stat-icon purple-icon">
            <TrendingUp />
          </div>
          <div className="stat-content">
            <span className="stat-label">Capitals</span>
            <span className="stat-number">{stats.totalCapitals}</span>
          </div>
        </div>
        
        <div className="stat-card" onClick={() => handleTabChange('otherOptions')}>
          <div className="stat-icon indigo-icon">
            <Assessment />
          </div>
          <div className="stat-content">
            <span className="stat-label">Other Options</span>
            <span className="stat-number">{stats.totalOtherOptions}</span>
          </div>
        </div>
        
        <div className="stat-card" onClick={() => handleTabChange('balanceSheetHeads')}>
          <div className="stat-icon purple-icon">
            <TrendingDown />
          </div>
          <div className="stat-content">
            <span className="stat-label">Balance Sheet Heads</span>
            <span className="stat-number">{stats.totalBalanceSheetHeads}</span>
          </div>
        </div>
                
      </div>
      
      {/* Debug Info - Remove this in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          background: '#f3f4f6', 
          padding: '10px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          fontSize: '11px',
          color: '#374151'
        }}>
          <strong>Debug Info:</strong> 
          Accounts: {accountHeads.length} | 
          Assets: {assets.length} | 
          Capitals: {capitals.length} | 
          Others: {otherOptions.length} | 
          Balance Sheets: {balanceSheetHeads.length} |
          Chart Data: C:{stats.capital}, A:{stats.accountAssets}, O:{stats.others} |
          Combined Status: Total:{stats.totalAllAccounts}, Active:{stats.activeAllAccounts}
        </div>
      )}      

      {/* Analytics Row */}
      <div className="analytics-row">
        {/* Account Categories Distribution */}
        <div className="analytics-card">
          <h3>Account Categories Distribution</h3>
          <div className="chart-section">
            <h4>Account Categories Breakdown</h4>
            <div className="bar-chart-container">
              <div className="bar-y-axis">
                <span>{Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1)}</span>
                <span>{Math.floor(Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1) / 2)}</span>
                <span>0</span>
              </div>
              <div className="bar-chart">
                <div className="bar-item">
                  <div 
                    className="bar" 
                    style={{
                      height: `${stats.totalAccounts > 0 ? (stats.totalAccounts / Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1)) * 100 : 5}%`, 
                      backgroundColor: '#3b82f6',
                      minHeight: '8px'
                    }} 
                    data-value={stats.totalAccounts}
                  ></div>
                  <span className="bar-label">Account Head</span>
                </div>
                <div className="bar-item">
                  <div 
                    className="bar" 
                    style={{
                      height: `${stats.capital > 0 ? (stats.capital / Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1)) * 100 : 5}%`, 
                      backgroundColor: '#ef4444',
                      minHeight: '8px'
                    }} 
                    data-value={stats.capital}
                  ></div>
                  <span className="bar-label">Capital</span>
                </div>
                <div className="bar-item">
                  <div 
                    className="bar" 
                    style={{
                      height: `${stats.accountAssets > 0 ? (stats.accountAssets / Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1)) * 100 : 5}%`, 
                      backgroundColor: '#22d3ee',
                      minHeight: '8px'
                    }} 
                    data-value={stats.accountAssets}
                  ></div>
                  <span className="bar-label">Assets</span>
                </div>
                <div className="bar-item">
                  <div 
                    className="bar" 
                    style={{
                      height: `${stats.others > 0 ? (stats.others / Math.max(stats.totalAccounts, stats.capital, stats.accountAssets, stats.others, 1)) * 100 : 5}%`, 
                      backgroundColor: '#f59e0b',
                      minHeight: '8px'
                    }} 
                    data-value={stats.others}
                  ></div>
                  <span className="bar-label">Others</span>
                </div>
              </div>
            </div>
            {(stats.totalAccounts === 0 && stats.capital === 0 && stats.accountAssets === 0 && stats.others === 0) && (
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '12px', marginTop: '10px' }}>
                No account data available
              </div>
            )}
          </div>
        </div>

        {/* Account Status */}
        <div className="analytics-card">
          <h3>Account Status Overview</h3>
          <div className="chart-section">
            <h4>Active vs Inactive Accounts</h4>
            <div className="chart-container">
              <div className="y-axis">
                <span>{Math.max(stats.totalAllAccounts, 1)}</span>
                <span>{Math.floor(Math.max(stats.totalAllAccounts, 1) / 2)}</span>
                <span>0</span>
              </div>
              <div className="chart-area">
                <svg width="100%" height="120" viewBox="0 0 400 120">
                  {/* Grid lines */}
                  <defs>
                    <pattern id="grid" width="40" height="30" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#f1f5f9" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                  
                  {/* Active accounts line */}
                  <polyline
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    points={`0,${120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} 100,${120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} 200,${120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} 300,${120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} 400,${120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100}`}
                  />
                  
                  {/* Inactive accounts line */}
                  <polyline
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="3"
                    points={`0,${120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} 100,${120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} 200,${120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} 300,${120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} 400,${120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100}`}
                  />
                  
                  {/* Data points for active accounts */}
                  <circle cx="0" cy={120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#10b981" />
                  <circle cx="100" cy={120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#10b981" />
                  <circle cx="200" cy={120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#10b981" />
                  <circle cx="300" cy={120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#10b981" />
                  <circle cx="400" cy={120 - (stats.activeAllAccounts / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#10b981" />
                  
                  {/* Data points for inactive accounts */}
                  <circle cx="0" cy={120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#ef4444" />
                  <circle cx="100" cy={120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#ef4444" />
                  <circle cx="200" cy={120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#ef4444" />
                  <circle cx="300" cy={120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#ef4444" />
                  <circle cx="400" cy={120 - ((stats.totalAllAccounts - stats.activeAllAccounts) / Math.max(stats.totalAllAccounts, 1)) * 100} r="4" fill="#ef4444" />
                </svg>
              </div>
            </div>
            <div className="x-axis">
              <span>Jan</span>
              <span>Feb</span>
              <span>Mar</span>
              <span>Apr</span>
              <span>May</span>
              <span>Jun</span>
            </div>
            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-dot" style={{backgroundColor: '#10b981'}}></span>
                <span>Active ({stats.activeAllAccounts})</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{backgroundColor: '#ef4444'}}></span>
                <span>Inactive ({stats.totalAllAccounts - stats.activeAllAccounts})</span>
              </div>
            </div>
            {stats.totalAllAccounts === 0 && (
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '12px', marginTop: '10px' }}>
                No account data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const renderAccountHeadTable = () => {
    const { data: paginatedData, totalItems, totalPages } = getPaginatedData(accountHeads, 'accountHead');
    
    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select
                value={filterActive}
                onChange={(e) => {
                  setFilterActive(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterBalanceSheetHead}
                onChange={(e) => {
                  setFilterBalanceSheetHead(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Balance Sheets</option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id || head.id} value={head.Id || head.id}>
                    {head.Name || head.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Desktop buttons with text */}
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('accountHead', 'create')}
              className="add-button desktop-only"
              disabled={loading}
            >
              Add Account
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Account">
              <IconButton onClick={() => handleOpenModal('accountHead', 'create')} className="add-icon-btn mobile-only" disabled={loading}>
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Name</th>
                <th>Balance Sheet</th>
                <th>Main Head</th>
                <th>VAT Rate</th>
                <th>SAC Code</th>
                <th>Income/Expense</th>
                <th>Trading</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span 
                      onClick={() => handleOpenModal('accountHead', 'edit', item)}
                      style={{ 
                        color: '#1e40af', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </span>
                  </td>
                  <td>{item.Name || '-'}</td>
                  <td>
                    {balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}
                  </td>
                  <td>{item.MainHeadId ? mainHeads.find(h => h.Id === item.MainHeadId)?.Name || '-' : '-'}</td>
                  <td>{item.VATRate ? `${item.VATRate}%` : '0%'}</td>
                  <td>{item.SACCode || '-'}</td>
                  <td>
                    <Chip
                      label={item.IsExpenseOrIncome ? 'Expense' : 'Income'}
                      color={item.IsExpenseOrIncome ? 'warning' : 'info'}
                      size="small"
                    />
                  </td>
                  <td>
                    <Chip
                      label={item.IsTrading ? 'Yes' : 'No'}
                      color={item.IsTrading ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>{item.Description || '-'}</td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('accountHead', 'view', item)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('accountHead', 'edit', item)}
                          disabled={loading || !item.IsActive || item.Id < 100}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {item.IsActive ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick('accountHead', item)}
                            disabled={loading || item.Id < 100}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivate('accountHead', item)}
                            disabled={loading}
                            sx={{ color: 'darkolivegreen' }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {renderPagination(totalPages, totalItems)}
      </div>
    );
  };
  const renderAssetTable = () => {
    const { data: paginatedData, totalItems, totalPages } = getPaginatedData(assets, 'asset');
    
    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select
                value={filterActive}
                onChange={(e) => {
                  setFilterActive(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterPayableReceivable}
                onChange={(e) => {
                  setFilterPayableReceivable(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Types</option>
                <option value="payable_receivable">Payable/Receivable</option>
                <option value="regular">Regular Assets</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterBalanceSheetHead}
                onChange={(e) => {
                  setFilterBalanceSheetHead(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Option Heads</option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id || head.id} value={head.Id || head.id}>
                    {head.Name || head.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('asset', 'create')}
              className="add-button desktop-only"
              disabled={loading}
            >
              Add Asset
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Asset">
              <IconButton onClick={() => handleOpenModal('asset', 'create')} className="add-icon-btn mobile-only" disabled={loading}>
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Name</th>
                <th>Balance Sheet Head</th>
                <th className="opening-balance-header">Opening Balance</th>
                <th>Opening Date</th>
                <th>Depreciation Rate</th>
                <th>P/R</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span 
                      onClick={() => handleOpenModal('asset', 'edit', item)}
                      style={{ 
                        color: '#1e40af', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </span>
                  </td>
                  <td>{item.Name || '-'}</td>
                  <td>
                    {balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}
                  </td>
                  <td className="opening-balance-cell">{(item.OpeningBalance || 0).toLocaleString()}</td>
                  <td>{formatDate(item.OpeningDate)}</td>
                  <td>{item.DepreciationRate ? `${item.DepreciationRate}%` : '-'}</td>
                  <td>
                    <Chip
                      label={item.IsPayableOrReceivable ? 'P/R' : 'Regular'}
                      color={item.IsPayableOrReceivable ? 'warning' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('asset', 'view', item)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('asset', 'edit', item)}
                          disabled={loading || !item.IsActive || item.Id < 100}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {item.IsActive ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick('asset', item)}
                            disabled={loading || item.Id < 100}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivate('asset', item)}
                            disabled={loading}
                            sx={{ color: 'darkolivegreen' }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {renderPagination(totalPages, totalItems)}
      </div>
    );
  };
  const renderCapitalTable = () => {
    console.log('Capitals State:', capitals);
    console.log('Other Options State:', otherOptions);
    const { data: paginatedData, totalItems, totalPages } = getPaginatedData(capitals, 'capital');
    
    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search capitals..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select
                value={filterActive}
                onChange={(e) => {
                  setFilterActive(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterPayableReceivable}
                onChange={(e) => {
                  setFilterPayableReceivable(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Types</option>
                <option value="payable_receivable">Payable/Receivable</option>
                <option value="regular">Regular</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterBalanceSheetHead}
                onChange={(e) => {
                  setFilterBalanceSheetHead(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Option Heads</option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id || head.id} value={head.Id || head.id}>
                    {head.Name || head.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('capital', 'create')}
              className="add-button desktop-only"
              disabled={loading}
            >
              Add Capital
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Capital">
              <IconButton onClick={() => handleOpenModal('capital', 'create')} className="add-icon-btn mobile-only" disabled={loading}>
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Name</th>
                <th>Searchable Name</th>
                <th>Balance Sheet Head</th>
                <th className="opening-balance-header">Opening Balance</th>
                <th>Opening Date</th>
                <th>P/R</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span 
                      onClick={() => handleOpenModal('capital', 'edit', item)}
                      style={{ 
                        color: '#1e40af', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </span>
                  </td>
                  <td>{item.Name || '-'}</td>
                  <td>{item.SearchableName || '-'}</td>
                  <td>
                    {balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}
                  </td>
                  <td className="opening-balance-cell">{(item.OpeningBalance || 0).toLocaleString()}</td>
                  <td>{formatDate(item.OpeningDate)}</td>
                  <td>
                    <Chip
                      label={item.IsPayableOrReceivable ? 'P/R' : 'Regular'}
                      color={item.IsPayableOrReceivable ? 'warning' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('capital', 'view', item)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('capital', 'edit', item)}
                          disabled={loading || !item.IsActive || item.Id < 100}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {item.IsActive ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick('capital', item)}
                            disabled={loading || item.Id < 100}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivate('capital', item)}
                            disabled={loading}
                            sx={{ color: 'darkolivegreen' }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {renderPagination(totalPages, totalItems)}
      </div>
    );
  };
  const renderOtherOptionTable = () => {
    const { data: paginatedData, totalItems, totalPages } = getPaginatedData(otherOptions, 'otherOption');
    
    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search other options..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select
                value={filterActive}
                onChange={(e) => {
                  setFilterActive(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterPayableReceivable}
                onChange={(e) => {
                  setFilterPayableReceivable(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Types</option>
                <option value="payable_receivable">Payable/Receivable</option>
                <option value="regular">Regular</option>
              </select>
            </div>
            <div className="filter-section">
              <select
                value={filterBalanceSheetHead}
                onChange={(e) => {
                  setFilterBalanceSheetHead(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Option Heads</option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id || head.id} value={head.Id || head.id}>
                    {head.Name || head.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('otherOption', 'create')}
              className="add-button desktop-only"
              disabled={loading}
            >
              Add Other Option
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Other Option">
              <IconButton onClick={() => handleOpenModal('otherOption', 'create')} className="add-icon-btn mobile-only" disabled={loading}>
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Name</th>
                <th>Searchable Name</th>
                <th>Balance Sheet Head</th>
                <th className="opening-balance-header">Opening Balance</th>
                <th>Opening Date</th>
                <th>P/R</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span 
                      onClick={() => handleOpenModal('otherOption', 'edit', item)}
                      style={{ 
                        color: '#1e40af', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </span>
                  </td>
                  <td>{item.Name || '-'}</td>
                  <td>{item.SearchableName || '-'}</td>
                  <td>
                    {balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}
                  </td>
                  <td className="opening-balance-cell">{(item.OpeningBalance || 0).toLocaleString()}</td>
                  <td>{formatDate(item.OpeningDate)}</td>
                  <td>
                    <Chip
                      label={item.IsPayableOrReceivable ? 'P/R' : 'Regular'}
                      color={item.IsPayableOrReceivable ? 'warning' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('otherOption', 'view', item)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('otherOption', 'edit', item)}
                          disabled={loading || !item.IsActive || item.Id < 100}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {item.IsActive ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick('otherOption', item)}
                            disabled={loading || item.Id < 100}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivate('otherOption', item)}
                            disabled={loading}
                            sx={{ color: 'darkolivegreen' }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {renderPagination(totalPages, totalItems)}
      </div>
    );
  };

  const renderBalanceSheetHeadTable = () => {
    const { data: paginatedData, totalItems, totalPages } = getPaginatedData(balanceSheetHeads, 'balanceSheetHead');
    
    return (
      <div className="data-table-container">
        <div className="table-header">
          <div className="search-filter-section">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search balance sheet heads..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="filter-section">
              <FilterList className="filter-icon" />
              <select
                value={filterActive}
                onChange={(e) => {
                  setFilterActive(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button desktop-only"
              startIcon={<Refresh />}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('balanceSheetHead', 'create')}
              className="add-button desktop-only"
              disabled={loading}
            >
              Add Balance Sheet Head
            </Button>
            {/* Mobile icon buttons */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Balance Sheet Head">
              <IconButton onClick={() => handleOpenModal('balanceSheetHead', 'create')} className="add-icon-btn mobile-only" disabled={loading}>
                <Add />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Name</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.Id}>
                  <td>
                    <span 
                      onClick={() => handleOpenModal('balanceSheetHead', 'edit', item)}
                      style={{ 
                        color: '#1e40af', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        textDecoration: 'underline'
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </span>
                  </td>
                  <td>{item.Name || '-'}</td>
                  <td>
                    <Chip
                      label={item.IsActive ? 'Active' : 'Inactive'}
                      color={item.IsActive ? 'success' : 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('balanceSheetHead', 'view', item)}
                          disabled={loading}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal('balanceSheetHead', 'edit', item)}
                          disabled={loading || !item.IsActive || item.Id < 100}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {item.IsActive ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick('balanceSheetHead', item)}
                            disabled={loading || item.Id < 100}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            onClick={() => handleActivate('balanceSheetHead', item)}
                            disabled={loading}
                            sx={{ color: 'darkolivegreen' }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {renderPagination(totalPages, totalItems)}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'accountHeads':
        return renderAccountHeadTable();
      case 'assets':
        return renderAssetTable();
      case 'capitals':
        return renderCapitalTable();
      case 'otherOptions':
        return renderOtherOptionTable();
      case 'balanceSheetHeads':
        return renderBalanceSheetHeadTable();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="account-head-dashboard">
      <div className="dashboard-header">
        <h1 className="plain-header">Accounts, Assets, Capitals, Other Options & Balance Sheet Heads Management</h1>
      </div>

      {activeTab !== 'overview' && (
        <div className="dashboard-tabs">
          <div className="tab-buttons">
            <button
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabChange('overview')}
            >
              Overview
            </button>
            <button
              className={`tab-button ${activeTab === 'accountHeads' ? 'active' : ''}`}
              onClick={() => handleTabChange('accountHeads')}
            >
              Account Head
            </button>
            <button
              className={`tab-button ${activeTab === 'assets' ? 'active' : ''}`}
              onClick={() => handleTabChange('assets')}
            >
              Assets
            </button>
            <button
              className={`tab-button ${activeTab === 'capitals' ? 'active' : ''}`}
              onClick={() => handleTabChange('capitals')}
            >
              Capitals
            </button>
            <button
              className={`tab-button ${activeTab === 'otherOptions' ? 'active' : ''}`}
              onClick={() => handleTabChange('otherOptions')}
            >
              Other Options
            </button>
            <button
              className={`tab-button ${activeTab === 'balanceSheetHeads' ? 'active' : ''}`}
              onClick={() => handleTabChange('balanceSheetHeads')}
            >
              Balance Sheet Heads
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {renderTabContent()}
      </div>

      {/* Professional Loading Overlay */}
      <LoadingOverlay 
        open={loading} 
        message={loadingMessage}
        variant={loadingVariant}
      />

      {/* Modal Components */}
      {formData.type === 'asset' ? (
        <AssetModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          selectedItem={selectedItem}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          balanceSheetHeads={balanceSheetHeads}
          loading={loading}
        />
      ) : formData.type === 'capital' ? (
        <CapitalModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          selectedItem={selectedItem}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          balanceSheetHeads={balanceSheetHeads}
          loading={loading}
        />
      ) : formData.type === 'otherOption' ? (
        <OtherOptionModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          selectedItem={selectedItem}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          balanceSheetHeads={balanceSheetHeads}
          loading={loading}
        />
      ) : formData.type === 'balanceSheetHead' ? (
        <BalanceSheetHeadModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          selectedItem={selectedItem}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          loading={loading}
        />
      ) : (
        <AccountHeadModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          selectedItem={selectedItem}
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          loading={loading}
          accountHeads={accountHeads}
          balanceSheetHeads={balanceSheetHeads}
          mainHeads={mainHeads}
        />
      )}

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Clean Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            padding: '32px 24px 24px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            background: 'white',
            maxWidth: '400px',
            margin: 'auto',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        {/* Trash Can Icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#ef4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}>
            <Delete sx={{ 
              fontSize: '40px', 
              color: 'white',
            }} />
            {/* Small decorative dots */}
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '15px',
              width: '6px',
              height: '6px',
              background: '#dc2626',
              borderRadius: '50%',
            }} />
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '25px',
              width: '4px',
              height: '4px',
              background: '#dc2626',
              borderRadius: '50%',
            }} />
          </div>
        </div>

        {/* Main Title */}
        <Typography
          variant="h5"
          sx={{
            color: '#1f2937',
            fontWeight: 700,
            marginBottom: '16px',
            fontSize: '28px',
            lineHeight: 1.2,
          }}
        >
          Are you sure?
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            marginBottom: '8px',
            fontSize: '16px',
            lineHeight: 1.4,
          }}
        >
          Do you really want to delete this {getEntityDisplayName(deleteType).toLowerCase()}?
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#6b7280',
            marginBottom: '32px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          This action cannot be undone!
        </Typography>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
        }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              minWidth: '120px',
              border: '2px solid #e5e7eb',
              background: '#f9fafb',
              '&:hover': {
                borderColor: '#9ca3af',
                background: '#f3f4f6',
                color: '#374151',
              },
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              background: '#ef4444',
              color: 'white',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              minWidth: '120px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover:not(:disabled)': {
                background: '#dc2626',
                boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default AccountHeadDashboard;
